import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Rocket, Package, CheckCircle, XCircle, Clock, ArrowRight, 
  Play, TestTube, Shield, AlertTriangle, RefreshCw, ChevronRight,
  Server, Activity, FileCode
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

export default function DeploymentStudio() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [transportLifecycles, setTransportLifecycles] = useState({});
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [approvalComments, setApprovalComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const ticketsRes = await axios.get(`${API}/tickets`);
      const allTickets = ticketsRes.data;

      // Fetch transport lifecycle for each ticket
      const lifecyclePromises = allTickets.map(async (ticket) => {
        try {
          const lifecycleRes = await axios.get(`${API}/transport/lifecycle/${ticket.id}`);
          return { ticketId: ticket.id, lifecycle: lifecycleRes.data };
        } catch (err) {
          return { ticketId: ticket.id, lifecycle: null };
        }
      });

      const lifecycles = await Promise.all(lifecyclePromises);
      const lifecycleMap = {};
      lifecycles.forEach(({ ticketId, lifecycle }) => {
        if (lifecycle) lifecycleMap[ticketId] = lifecycle;
      });

      setTickets(allTickets);
      setTransportLifecycles(lifecycleMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupTicketsByStage = () => {
    const groups = {
      ready_for_qa: [],
      ready_for_prod: [],
      in_dev: [],
      in_qa_testing: [],
      deployed: []
    };

    tickets.forEach(ticket => {
      const lifecycle = transportLifecycles[ticket.id];
      
      if (!lifecycle) {
        groups.in_dev.push(ticket);
      } else if (lifecycle.dev_status?.status === 'ready_for_transport' || lifecycle.dev_status?.test_status === 'passed') {
        groups.ready_for_qa.push(ticket);
      } else if (lifecycle.qa_status?.status === 'ready_for_prod' || lifecycle.qa_status?.test_status === 'passed') {
        groups.ready_for_prod.push(ticket);
      } else if (lifecycle.qa_status?.status === 'testing') {
        groups.in_qa_testing.push(ticket);
      } else if (lifecycle.prod_status?.status === 'deployed') {
        groups.deployed.push(ticket);
      } else {
        groups.in_dev.push(ticket);
      }
    });

    return groups;
  };

  const deployToQA = async (ticketId) => {
    try {
      const lifecycle = transportLifecycles[ticketId];
      
      // Create transport if not exists
      if (!lifecycle.dev_status.transport_request_id) {
        await axios.post(`${API}/transport/lifecycle/${ticketId}/action`, {
          action: 'create_transport_to_qa',
          notes: `Transport created from Deployment Studio`
        });
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      // Import to QA
      const updatedLifecycle = await axios.get(`${API}/transport/lifecycle/${ticketId}`);
      const transportId = updatedLifecycle.data.dev_status.transport_request_id;
      
      await axios.post(`${API}/transport/lifecycle/${ticketId}/action`, {
        action: 'import_to_qa',
        notes: transportId
      });
      
      await fetchData();
      alert('Successfully deployed to QA!');
    } catch (error) {
      console.error('Error deploying to QA:', error);
      alert('Error deploying to QA: ' + (error.response?.data?.detail || error.message));
    }
  };

  const runHealthCheck = async (ticketId, environment) => {
    setTesting({ ...testing, [`${ticketId}_${environment}`]: true });
    
    try {
      // Simulate health check - in production, this would:
      // 1. Call SAP system API endpoints
      // 2. Check OData service availability
      // 3. Verify deployed objects exist
      // 4. Test basic CRUD operations
      
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const passed = Math.random() > 0.2; // 80% success rate for demo
      
      // Update test status
      const action = environment === 'dev' ? 'complete_dev_testing' : 'complete_qa_testing';
      await axios.post(`${API}/transport/lifecycle/${ticketId}/action`, {
        action: action,
        test_results: {
          passed: passed,
          test_type: 'health_check',
          checks: [
            { name: 'API Connectivity', status: passed ? 'passed' : 'failed' },
            { name: 'OData Service Available', status: 'passed' },
            { name: 'Authentication', status: passed ? 'passed' : 'failed' },
            { name: 'Basic CRUD Operations', status: 'passed' }
          ]
        },
        notes: `Health check ${passed ? 'passed' : 'failed'} in ${environment.toUpperCase()}`
      });
      
      await fetchData();
      alert(`Health check ${passed ? 'passed ✓' : 'failed ✗'} in ${environment.toUpperCase()}`);
    } catch (error) {
      console.error('Error running health check:', error);
      alert('Error running health check: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTesting({ ...testing, [`${ticketId}_${environment}`]: false });
    }
  };

  const requestProdApproval = async (ticketId) => {
    const ticket = tickets.find(t => t.id === ticketId);
    setSelectedTicket(ticket);
    setApprovalDialogOpen(true);
  };

  const approveProdDeployment = async () => {
    if (!selectedTicket) return;
    
    try {
      // Create transport to PROD
      await axios.post(`${API}/transport/lifecycle/${selectedTicket.id}/action`, {
        action: 'create_transport_to_prod',
        notes: `Approved for PROD deployment. ${approvalComments}`
      });
      
      await fetchData();
      setApprovalDialogOpen(false);
      setApprovalComments('');
      alert('PROD deployment approved! Transport created.');
    } catch (error) {
      console.error('Error approving deployment:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const deployToProd = async (ticketId) => {
    try {
      const lifecycle = transportLifecycles[ticketId];
      const transportId = lifecycle.qa_status.transport_request_id;
      
      await axios.post(`${API}/transport/lifecycle/${ticketId}/action`, {
        action: 'import_to_prod',
        notes: transportId
      });
      
      await fetchData();
      alert('Successfully deployed to PROD! 🚀');
    } catch (error) {
      console.error('Error deploying to PROD:', error);
      alert('Error deploying to PROD: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  const groups = groupTicketsByStage();

  return (
    <div className="space-y-6" data-testid="deployment-studio">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Deployment Studio</h1>
          <p className="text-muted-foreground mt-2">Manage transport lifecycle and deployments across landscapes</p>
        </div>
        <Button variant="outline" onClick={() => fetchData()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Overview Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Development</CardTitle>
            <FileCode className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{groups.in_dev.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready for QA</CardTitle>
            <Package className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{groups.ready_for_qa.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ready for PROD</CardTitle>
            <Shield className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{groups.ready_for_prod.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Deployed</CardTitle>
            <Rocket className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{groups.deployed.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Deployment Pipeline Tabs */}
      <Tabs defaultValue="ready_qa" className="w-full">
        <TabsList>
          <TabsTrigger value="ready_qa">
            <Package className="h-4 w-4 mr-2" />
            Ready for QA ({groups.ready_for_qa.length})
          </TabsTrigger>
          <TabsTrigger value="ready_prod">
            <Shield className="h-4 w-4 mr-2" />
            Ready for PROD ({groups.ready_for_prod.length})
          </TabsTrigger>
          <TabsTrigger value="deployed">
            <Rocket className="h-4 w-4 mr-2" />
            Deployed ({groups.deployed.length})
          </TabsTrigger>
          <TabsTrigger value="in_progress">
            <Activity className="h-4 w-4 mr-2" />
            In Progress ({groups.in_dev.length + groups.in_qa_testing.length})
          </TabsTrigger>
        </TabsList>

        {/* Ready for QA Tab */}
        <TabsContent value="ready_qa" className="space-y-4">
          {groups.ready_for_qa.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No tickets ready for QA deployment
              </CardContent>
            </Card>
          ) : (
            groups.ready_for_qa.map(ticket => (
              <DeploymentCard
                key={ticket.id}
                ticket={ticket}
                lifecycle={transportLifecycles[ticket.id]}
                stage="qa"
                onDeploy={() => deployToQA(ticket.id)}
                onTest={() => runHealthCheck(ticket.id, 'qa')}
                onViewDetails={() => navigate(`/tickets/${ticket.ticket_number}`)}
                testing={testing[`${ticket.id}_qa`]}
              />
            ))
          )}
        </TabsContent>

        {/* Ready for PROD Tab */}
        <TabsContent value="ready_prod" className="space-y-4">
          {groups.ready_for_prod.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No tickets ready for PROD deployment
              </CardContent>
            </Card>
          ) : (
            groups.ready_for_prod.map(ticket => (
              <DeploymentCard
                key={ticket.id}
                ticket={ticket}
                lifecycle={transportLifecycles[ticket.id]}
                stage="prod"
                onApprove={() => requestProdApproval(ticket.id)}
                onDeploy={() => deployToProd(ticket.id)}
                onTest={() => runHealthCheck(ticket.id, 'prod')}
                onViewDetails={() => navigate(`/tickets/${ticket.ticket_number}`)}
                testing={testing[`${ticket.id}_prod`]}
              />
            ))
          )}
        </TabsContent>

        {/* Deployed Tab */}
        <TabsContent value="deployed" className="space-y-4">
          {groups.deployed.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No deployed tickets yet
              </CardContent>
            </Card>
          ) : (
            groups.deployed.map(ticket => (
              <DeploymentCard
                key={ticket.id}
                ticket={ticket}
                lifecycle={transportLifecycles[ticket.id]}
                stage="deployed"
                onViewDetails={() => navigate(`/tickets/${ticket.ticket_number}`)}
              />
            ))
          )}
        </TabsContent>

        {/* In Progress Tab */}
        <TabsContent value="in_progress" className="space-y-4">
          {[...groups.in_dev, ...groups.in_qa_testing].map(ticket => (
            <DeploymentCard
              key={ticket.id}
              ticket={ticket}
              lifecycle={transportLifecycles[ticket.id]}
              stage="dev"
              onTest={() => runHealthCheck(ticket.id, 'dev')}
              onViewDetails={() => navigate(`/tickets/${ticket.ticket_number}`)}
              testing={testing[`${ticket.id}_dev`]}
            />
          ))}
        </TabsContent>
      </Tabs>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-green-600" />
              Approve Production Deployment
            </DialogTitle>
            <DialogDescription>
              Review and approve deployment to production environment
            </DialogDescription>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="font-semibold mb-2">{selectedTicket.ticket_number}: {selectedTicket.title}</div>
                <div className="text-sm text-muted-foreground">{selectedTicket.description}</div>
                <div className="mt-2 flex gap-2">
                  <Badge>🎯 {selectedTicket.target_system_name || selectedTicket.sap_system}</Badge>
                  <Badge variant="outline">{selectedTicket.customization_type}</Badge>
                </div>
              </div>

              {/* Governance Review */}
              <div className="p-4 border rounded-lg">
                <div className="font-semibold mb-2">Governance Review</div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Clean Core Compliance:</span>
                    <Badge className="ml-2">✓ Compliant</Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Security Review:</span>
                    <Badge className="ml-2">✓ Passed</Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">QA Test Status:</span>
                    <Badge className="ml-2 bg-green-600">✓ Passed</Badge>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Code Quality:</span>
                    <Badge className="ml-2">95%</Badge>
                  </div>
                </div>
              </div>

              <div>
                <Label>Approval Comments</Label>
                <Textarea
                  value={approvalComments}
                  onChange={(e) => setApprovalComments(e.target.value)}
                  placeholder="Add any comments or notes about this approval..."
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={approveProdDeployment} className="flex-1 bg-green-600 hover:bg-green-700">
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve & Create PROD Transport
                </Button>
                <Button variant="outline" onClick={() => setApprovalDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Deployment Card Component
function DeploymentCard({ ticket, lifecycle, stage, onDeploy, onTest, onApprove, onViewDetails, testing }) {
  const getStageIcon = () => {
    if (stage === 'deployed') return <Rocket className="h-5 w-5 text-purple-600" />;
    if (stage === 'prod') return <Shield className="h-5 w-5 text-green-600" />;
    if (stage === 'qa') return <Package className="h-5 w-5 text-amber-600" />;
    return <FileCode className="h-5 w-5 text-blue-600" />;
  };

  const getTestStatusBadge = (testStatus) => {
    if (testStatus === 'passed') return <Badge className="bg-green-600">✓ Tests Passed</Badge>;
    if (testStatus === 'failed') return <Badge variant="destructive">✗ Tests Failed</Badge>;
    if (testStatus === 'in_progress') return <Badge>Testing...</Badge>;
    return <Badge variant="outline">Not Tested</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {getStageIcon()}
              <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
              <Badge className="text-xs">{ticket.status.replace('_', ' ')}</Badge>
              {ticket.target_system_name && (
                <Badge className="text-xs bg-blue-600">
                  🎯 {ticket.target_system_name}
                </Badge>
              )}
            </div>
            <CardTitle className="text-lg">{ticket.title}</CardTitle>
            <CardDescription className="mt-1">{ticket.description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Transport Pipeline Visual */}
          {lifecycle && (
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">Transport Pipeline</span>
                <Badge variant="outline">{lifecycle.overall_status.replace('_', ' ').toUpperCase()}</Badge>
              </div>
              
              <div className="grid grid-cols-5 gap-2 items-center">
                {/* DEV */}
                <div className="text-center">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-1 flex items-center justify-center ${
                    lifecycle.dev_status.code_complete ? 'bg-green-500' : 'bg-slate-300'
                  }`}>
                    {lifecycle.dev_status.code_complete ? (
                      <CheckCircle className="h-6 w-6 text-white" />
                    ) : (
                      <Clock className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="text-xs font-medium">DEV</div>
                  {lifecycle.dev_status.test_status && (
                    <div className="text-xs mt-1">{getTestStatusBadge(lifecycle.dev_status.test_status)}</div>
                  )}
                </div>

                <ArrowRight className={`h-5 w-5 ${lifecycle.dev_status.transport_request_id ? 'text-green-600' : 'text-slate-300'}`} />

                {/* QA */}
                <div className="text-center">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-1 flex items-center justify-center ${
                    lifecycle.qa_status.deployed_at ? 'bg-green-500' : 'bg-slate-300'
                  }`}>
                    {lifecycle.qa_status.deployed_at ? (
                      <CheckCircle className="h-6 w-6 text-white" />
                    ) : (
                      <Clock className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="text-xs font-medium">QA</div>
                  {lifecycle.qa_status.test_status && (
                    <div className="text-xs mt-1">{getTestStatusBadge(lifecycle.qa_status.test_status)}</div>
                  )}
                </div>

                <ArrowRight className={`h-5 w-5 ${lifecycle.qa_status.transport_request_id ? 'text-green-600' : 'text-slate-300'}`} />

                {/* PROD */}
                <div className="text-center">
                  <div className={`w-12 h-12 rounded-full mx-auto mb-1 flex items-center justify-center ${
                    lifecycle.prod_status.deployed_at ? 'bg-purple-500' : 'bg-slate-300'
                  }`}>
                    {lifecycle.prod_status.deployed_at ? (
                      <Rocket className="h-6 w-6 text-white" />
                    ) : (
                      <Clock className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div className="text-xs font-medium">PROD</div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {stage === 'qa' && onDeploy && (
              <>
                <Button onClick={onDeploy} size="sm" className="bg-amber-600 hover:bg-amber-700">
                  <ChevronRight className="mr-2 h-4 w-4" />
                  Deploy to QA
                </Button>
                {onTest && (
                  <Button onClick={onTest} size="sm" variant="outline" disabled={testing}>
                    {testing ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <TestTube className="mr-2 h-4 w-4" />
                    )}
                    Health Check
                  </Button>
                )}
              </>
            )}
            
            {stage === 'prod' && (
              <>
                {onApprove && !lifecycle?.qa_status?.transport_request_id && (
                  <Button onClick={onApprove} size="sm" className="bg-green-600 hover:bg-green-700">
                    <Shield className="mr-2 h-4 w-4" />
                    Request PROD Approval
                  </Button>
                )}
                {onDeploy && lifecycle?.qa_status?.transport_request_id && (
                  <Button onClick={onDeploy} size="sm" className="bg-purple-600 hover:bg-purple-700">
                    <Rocket className="mr-2 h-4 w-4" />
                    Deploy to PROD
                  </Button>
                )}
              </>
            )}
            
            {stage === 'dev' && onTest && (
              <Button onClick={onTest} size="sm" variant="outline" disabled={testing}>
                {testing ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <TestTube className="mr-2 h-4 w-4" />
                )}
                Run Health Check
              </Button>
            )}
            
            <Button onClick={onViewDetails} size="sm" variant="outline">
              View Details
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
